import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import ScriptCoGeneralAndContactDetails from "./ScriptCoGeneralAndContactDetails";
import ScriptCoPatientAddressAndNotes from "./ScriptCoPatientAddressAndNotes";

const ScriptCoAddressDetails = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const [address, setAddress] = useState(data.billing || data.delivery || {});

  React.useEffect(() => {
    if (data.billing) setAddress(data.billing);
  }, [data.billing]);

  const handleAddressChange = useCallback(
    (field, value) => {
      setAddress((prev) => {
        const updated = { ...prev, [field]: value };
        onDataChange("billing", updated);
        onDataChange("delivery", updated);
        return updated;
      });
    },
    [onDataChange],
  );

  const handleScriptCoDetailsChange = useCallback(
    (field, value) => {
      onDataChange("scriptCoDetails", {
        ...data.scriptCoDetails,
        [field]: value,
      });
    },
    [onDataChange, data.scriptCoDetails],
  );

  const handleNotesChange = useCallback(
    (value) => {
      handleScriptCoDetailsChange("patientNotes", value);
    },
    [handleScriptCoDetailsChange],
  );

  const onGeneralDataChange = useCallback(
    (newData) => onDataChange("general", newData),
    [onDataChange],
  );

  const handlePatientDocumentChange = useCallback(
    (fileData) => {
      onDataChange("patientDocument", fileData);
    },
    [onDataChange],
  );

  const [isPatientDocValid, setIsPatientDocValid] = useState(false);
  const [isGeneralValid, setIsGeneralValid] = useState(true);
  const [isAddressValid, setIsAddressValid] = useState(false);

  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isPatientDocValid && isGeneralValid && isAddressValid);
    }
  }, [isPatientDocValid, isGeneralValid, isAddressValid, onValidationChange]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}
    >
      <ScriptCoGeneralAndContactDetails
        opportunity={opportunity}
        data={data.general}
        onDataChange={onGeneralDataChange}
        onValidationChange={setIsGeneralValid}
        showErrors={showErrors}
        patientDocument={data.patientDocument}
        onPatientDocumentChange={handlePatientDocumentChange}
        onPatientDocValidationChange={setIsPatientDocValid}
      />
      <ScriptCoPatientAddressAndNotes
        address={address}
        patientNotes={data.scriptCoDetails?.patientNotes || ""}
        isBillingAddress={data.scriptCoDetails?.isBillingAddress ?? false}
        onAddressChange={handleAddressChange}
        onNotesChange={handleNotesChange}
        onIsBillingAddressChange={(val) =>
          handleScriptCoDetailsChange("isBillingAddress", val)
        }
        onValidationChange={setIsAddressValid}
        showErrors={showErrors}
      />
    </Box>
  );
};

ScriptCoAddressDetails.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default ScriptCoAddressDetails;
